import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { searchArticles } from '@/utils/content';

const hotWords = ['游说金流', '离岸架构', '招标', '计票', '财报', '评审'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);

  const results = useMemo(() => searchArticles(query), [query]);

  useSeo({
    title: query ? `检索「${query}」| 美日吃瓜` : '站内检索 | 美日吃瓜',
    description: query
      ? `在美日吃瓜站内检索「${query}」，共找到 ${results.length} 篇相关档案，覆盖政客丑闻、财阀内幕、官员贪腐、选举黑幕、企业造假与学术不端六大栏目。`
      : '使用关键词检索美日吃瓜的六大栏目档案，快速定位政客丑闻、财阀内幕、官员贪腐等主题的深度内容。',
    keywords: '美日吃瓜站内检索,关键词搜索,政经档案检索',
    path: '/search',
  });

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = keyword.trim();
    if (!next) {
      setParams({});
      return;
    }
    setParams({ q: next });
  };

  return (
    <>
      <Breadcrumbs items={[{ label: '站内检索' }]} />

      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <p className="mb-3 font-label text-xs uppercase tracking-[0.32em] text-primary-600">
            Search
          </p>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            站内检索
          </h1>
          <form onSubmit={handleSearch} className="mt-6 max-w-2xl">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入标题、机构或关键词，例如：离岸架构"
                  className="w-full rounded-full border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                开始检索
              </button>
            </div>
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs text-foreground-500">热门关键词</span>
            {hotWords.map((word) => (
              <Link
                key={word}
                to={`/search?q=${encodeURIComponent(word)}`}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {word}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        {query ? (
          <>
            <p className="mb-6 font-label text-sm text-foreground-600">
              关键词「<span className="font-semibold text-foreground-950">{query}</span>」共找到{' '}
              <span className="font-semibold text-primary-600">{results.length}</span> 篇档案
            </p>
            {results.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-300/70 bg-background-100 px-6 py-14 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
                  <i className="ri-search-eye-line" />
                </span>
                <p className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  没有匹配的档案
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  换一个更短的关键词试试，或者直接按栏目浏览，也许更容易找到你要的线索。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {categories.slice(0, 4).map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      className="rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-background-300/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">按栏目浏览</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              还没想好搜什么？六个固定栏目就是我们全部的内容结构，从任意一条线索开始都可以。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="group flex items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={category.icon} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {category.name}
                    </span>
                    <span className="mt-0.5 block font-label text-xs text-foreground-500">
                      {category.count} 篇档案
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}